import {
  ChangeDetectorRef,
  Component, ComponentRef,
  EventEmitter,
  Input,
  Output, SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { SharedModule } from "../../modules/shared/shared.module";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { StatsData } from "../../models/stats-response.interface";
import { FilterCondition, SearchBarConfig, TableListRequest } from "../../models/table-list-request.interface";
import { PaginatedListDataResponse } from "../../models/paginated-list-data-response";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { CommonUtils } from "../../utils/common-utils";
import { NoDataFoundComponent } from "../no-data-found/no-data-found.component";
import { ModalComponent } from "../modal/modal.component";
import { AdvancedFilterComponentV2, AdvancedFilterOutput } from "../advanced-filter/advanced-filter-v2.component";
import { AdvancedFilterFieldInfo } from "../../models/advanced-filter-config.interface";
import vesselMasterConfig from "../masters/vessel-port-train-details-master/config/vessel-master-config.json";
import { Where } from "../../models/user-list-request.interface";

export const SEARCH_BAR_ALIAS_NAME = "SEARCH_BAR";

const INVOICE_STATUS = {
  PENDING: "PENDING",
  PAID: "PAID",
  CREATED: "CREATED",
  PARTIALLY_PAID: "PARTIALLY_PAID"
}

@Component({
  selector: "app-list-details-panel",
  standalone: true,
  imports: [
    SharedModule,
    SvgIconComponent,
    MatPaginator
  ],
  templateUrl: "./list-details-panel.component.html",
  styleUrl: "./list-details-panel.component.scss"
})
export class ListDetailsPanelComponent {
  protected readonly CommonUtils = CommonUtils;
  searchTerm: string = "";
  previousTerm: string = "";
  rawFilterData: [] = [];

  @Input() title = "";
  @Input() dataListRequest: TableListRequest = {
    limit: 10,
    page: 0
  };
  @Input() dataListResponse: PaginatedListDataResponse = {
    totalCount: 0,
    page: 0,
    limit: 0,
    records: []
  };
  @Input() allTableRecordsSelected: boolean = false;
  @Input() searchBarConfig!: SearchBarConfig;
  @Input() openedCardId: string = "";
  @Input() showSecondaryActionsBtn: boolean = false;
  @Input() primaryActionBtnText: string = "New";
  @Input() secondaryActionBtnText: string = "New";
  @Input() filterConfig: AdvancedFilterFieldInfo[] = [];

  //key from dataListResponse used for card title and subtitle
  @Input() cardTitleKey = "";
  @Input() cardSubTitleKey = "";
  @Input() keysToDisplay: string[] = [];

  @Output() primaryButtonClick = new EventEmitter<void>();
  @Output() secondaryButtonClick = new EventEmitter<void>();
  @Output() dataListRequestChange = new EventEmitter<TableListRequest>();
  @Output() tosTableAction = new EventEmitter<any>();
  @Output() cardOpened = new EventEmitter<any>();
  //TODO: Handle checkbox selection
  @Output() selectedItemsChange = new EventEmitter<any[]>();
  @Output() backButtonClick = new EventEmitter<void>();

  @ViewChild("rightPanel", { read: ViewContainerRef, static: false }) rightPanel!: ViewContainerRef;
  protected isFilterApplied: boolean = false;

  constructor(private cdr: ChangeDetectorRef,
              private viewContainerRef: ViewContainerRef) {
  }

  ngOnInit() {
    this.searchTerm = this.dataListRequest.where?.filterConditions.find((condition: FilterCondition) => condition.alias.endsWith(SEARCH_BAR_ALIAS_NAME))?.value[0] || "";
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["dataListResponse"] && changes["dataListResponse"].currentValue) {
      if (this.dataListResponse?.records?.length > 0) {
        this.setupCard(this.openedCardId);
      } else {
        this.loadDefaultComponent();
      }
    }
  }


  getKeys(record: any): string[] {
    return Object.keys(record).filter(key => this.keysToDisplay.includes(key));
  }

  onPageChange(pageEvent: PageEvent) {
    this.dataListRequest.page = pageEvent.pageIndex;
    this.dataListRequest.limit = pageEvent.pageSize;
    this.tosTableAction.emit();
  }

  handleTableSearch(event: KeyboardEvent) {
    const searchTerm = this.searchTerm.trim();

    if (searchTerm.length > 0) {
      const filterConditions = this.getFilterConditionsFromSearchBarConfig(searchTerm);
      const expression = this.getExpressionFromSearchBarConfig(filterConditions);
      this.dataListRequest.where = {
        filterConditions,
        expression
      };

      // Search when Enter is pressed
      if (event.key === "Enter") {
        this.tosTableAction.emit();
      }
    }

    // Call reset only when the search bar is cleared (avoid on multiple backspaces)
    if (searchTerm.length === 0 && this.previousTerm.length !== 0) {
      delete this.dataListRequest.where;
      this.tosTableAction.emit();
    }
    this.previousTerm = searchTerm;
  }

  loadComponentsInRightPanel(components: Array<{
    component: Type<any>,
    data?: Partial<any>
  }>): Array<ComponentRef<any>> {
    const componentRefs: Array<ComponentRef<any>> = [];
    if (this.rightPanel) {
      this.rightPanel.clear();
      components.forEach(({ component, data }) => {
        const componentRef = this.rightPanel.createComponent(component);
        componentRef.location.nativeElement.style.width = "100%";
        if (componentRef.instance) {
          if (data) {
            Object.assign(componentRef.instance, data);
          }
        }
        componentRefs.push(componentRef);
      });
    } else {
      console.warn("rightPanel is not available.");
    }
    return componentRefs;
  }

  setupCard(recordId: any) {
    this.openedCardId = recordId;
    this.cardOpened.emit(recordId);
  }

  private getExpressionFromSearchBarConfig(filterConditions: FilterCondition[]) {
    return filterConditions.map((config, index) => {
      return `${config.alias}`;
    }).join(" OR ");
  }

  private getFilterConditionsFromSearchBarConfig(searchTerm: string) {
    return this.searchBarConfig.filters.map((config) => {
      return {
        ...config,
        alias: `${config.alias}${SEARCH_BAR_ALIAS_NAME}`,
        value: [searchTerm]
      };
    });
  }

  private loadDefaultComponent() {
    this.loadComponentsInRightPanel([{ component: NoDataFoundComponent, data: { msg: "No Records Found" } }]);
  }

  openFilters() {
    const modalRef = this.viewContainerRef.createComponent(ModalComponent);
    modalRef.instance.title = "Advanced Filter";
    const fieldInfoList: AdvancedFilterFieldInfo[] = CommonUtils.getFilterFieldList(this.filterConfig);

    const filterDialogRef = modalRef.instance.loadComponent(
      AdvancedFilterComponentV2, {
        filterFields: fieldInfoList,
        useNewContract: true,
        appliedFilters: this.rawFilterData
      }
    );

    modalRef.instance.close.subscribe((res: AdvancedFilterOutput) => {
      if (!res) {
        modalRef.destroy();
        return;
      }

      if (res.resetAll) {
        delete this.dataListRequest.where;
        this.rawFilterData = [];
        this.isFilterApplied = false;
        this.tosTableAction.emit();
      } else if (res.refresh) {
        if (res.filterData?.expression && res.filterData.filterConditions.length > 0) {
          this.rawFilterData = res.rawFilterData;

          const mergedFilterConditions = [
            ...res.filterData.filterConditions,
            ...(this.dataListRequest.where?.filterConditions ?? [])
          ];

          this.dataListRequest.where = {
            ...this.dataListRequest.where,
            filterConditions: mergedFilterConditions,
            expression: this.getExpressionFromSearchBarConfig(mergedFilterConditions)
          };

          this.isFilterApplied = true;
        } else {
          console.error("Invalid filter!");
          delete this.dataListRequest.where;
        }
        this.tosTableAction.emit();
      }

      modalRef.destroy();
    });


  }

  getStatusString(status: string) {
    if(status === INVOICE_STATUS.PAID){
      return "Paid";
    }else if(status === INVOICE_STATUS.CREATED){
      return "Invoice Generated";
    }else if(status === INVOICE_STATUS.PENDING){
      return "Pending";
    }else if(status === INVOICE_STATUS.PARTIALLY_PAID){
      return "Partially Paid";
    }else {
      return status;
    }
  }

  protected readonly INVOICE_STATUS = INVOICE_STATUS;
}
